import { computed, ref } from 'vue'
import { apiFetch } from '../../services/api'
import type { CanvasNodeKind } from './salesPlayCanvasContext'
import { getCanvasNodeDef, type CanvasNode, type CanvasNodeDefinition } from './salesPlayNodeRegistry'

// ── Singleton draft state ───────────────────────────────────────

const _playId = ref('')
const _pipelineId = ref<string | null>(null)  // legacy — kept for backward compat reads
const _sequenceAgentId = ref<string | null>(null)
const _sequenceId = ref<string | null>(null)
const _actionIds = ref<Record<string, string>>({})
const _hadProspectingAgent = ref(false)
const _nodes = ref<CanvasNode[]>([])
const _savedSnapshot = ref('')
const _saving = ref(false)
const _error = ref('')
// The step types the play's sequence may have; null means any.
const _stepTypes = ref<string[] | null>(null)

// Snapshots of play-level collections at load time, for diffing on save.
const _savedPersonaIds = ref<Set<string>>(new Set())
const _savedFilterOrder = ref<string[]>([])
const _savedSourceIds = ref<Set<string>>(new Set())
const _savedSourceOrder = ref<string[]>([])

function snapshot(): string {
  return JSON.stringify(_nodes.value)
}

interface DraftOpts {
  pipelineId?: string | null
  sequenceAgentId?: string | null
  sequenceId?: string | null
  actionIds?: Record<string, string>
  stepTypes?: string[] | null
}

// ── Public composable ───────────────────────────────────────────

export function useSalesPlayDraft() {
  const nodes = computed(() => _nodes.value)
  const dirty = computed(() => snapshot() !== _savedSnapshot.value)
  const saving = computed(() => _saving.value)
  const error = computed(() => _error.value)

  /** Initialize the draft from backend data. */
  function initialize(
    playId: string,
    opts: DraftOpts,
    nodesFromBackend: CanvasNode[],
  ) {
    _playId.value = playId
    _pipelineId.value = opts.pipelineId || null
    _sequenceAgentId.value = opts.sequenceAgentId || null
    _sequenceId.value = opts.sequenceId || null
    _actionIds.value = { ...(opts.actionIds || {}) }
    _stepTypes.value = opts.stepTypes ?? null
    _hadProspectingAgent.value = nodesFromBackend.some((n) => n.kind === 'prospect')
    _nodes.value = JSON.parse(JSON.stringify(nodesFromBackend))
    _savedSnapshot.value = snapshot()
    _error.value = ''

    // Snapshot existing IDs for diffing on save.
    _savedPersonaIds.value = new Set<string>()
    _savedFilterOrder.value = nodesFromBackend
      .filter((n) => n.kind === 'filter' && n.config.id)
      .map((n) => n.config.id)
    _savedSourceIds.value = new Set<string>(
      nodesFromBackend.filter((n) => n.kind === 'source' && n.config.id).map((n) => n.config.id),
    )
    _savedSourceOrder.value = nodesFromBackend
      .filter((n) => n.kind === 'source' && n.config.id)
      .map((n) => n.config.id)
    for (const node of nodesFromBackend) {
      if (node.kind === 'prospect') {
        for (const p of node.config.personas || []) {
          if (p.id) _savedPersonaIds.value.add(p.id)
        }
      }
    }
  }

  /** Refresh draft from polled backend data without losing selection.
   *
   *  Matches incoming nodes to existing ones by type+kind. If the set of
   *  nodes changed (added/removed), does a full re-initialize. If only
   *  configs changed, updates in place so node IDs (and thus selection)
   *  are preserved.
   */
  function refreshFromBackend(
    incomingNodes: CanvasNode[],
    opts: DraftOpts,
  ) {
    // Quick structural check: same count and same type sequence?
    const current = _nodes.value
    const sameStructure =
      current.length === incomingNodes.length &&
      current.every((n, i) => n.type === incomingNodes[i].type && n.kind === incomingNodes[i].kind)

    if (!sameStructure) {
      // Structure changed (node added/removed) — full re-init.
      initialize(_playId.value, opts, incomingNodes)
      return
    }

    // Same structure — update configs in place, preserving node IDs.
    _pipelineId.value = opts.pipelineId || null
    _sequenceAgentId.value = opts.sequenceAgentId || null
    _sequenceId.value = opts.sequenceId || null
    _actionIds.value = { ...(opts.actionIds || {}) }
    _hadProspectingAgent.value = incomingNodes.some((n) => n.kind === 'prospect')
    _savedFilterOrder.value = incomingNodes
      .filter((n) => n.kind === 'filter' && n.config.id)
      .map((n) => n.config.id)
    _savedSourceIds.value = new Set<string>(
      incomingNodes.filter((n) => n.kind === 'source' && n.config.id).map((n) => n.config.id),
    )
    _savedSourceOrder.value = incomingNodes
      .filter((n) => n.kind === 'source' && n.config.id)
      .map((n) => n.config.id)
    let changed = false
    for (let i = 0; i < current.length; i++) {
      const incoming = incomingNodes[i]
      const existing = current[i]
      const incomingJson = JSON.stringify(incoming.config)
      const existingJson = JSON.stringify(existing.config)
      if (incomingJson !== existingJson) {
        existing.config = JSON.parse(incomingJson)
        changed = true
      }
    }
    // Trigger reactivity on the ref — deep property changes on array
    // elements don't trigger ref watchers automatically.
    if (changed) {
      _nodes.value = [..._nodes.value]
    }
    _savedSnapshot.value = snapshot()
  }

  /** Add a node from a registry definition. Returns the new node's index. */
  function addNode(def: CanvasNodeDefinition, insertAt?: number | null): number {
    const node: CanvasNode = {
      id: `${def.type}-${Date.now()}`,
      type: def.type,
      kind: def.kind,
      actionType: def.actionType,
      config: def.createDefaultConfig(),
    }
    if (insertAt != null && insertAt >= 0 && insertAt <= _nodes.value.length) {
      _nodes.value.splice(insertAt, 0, node)
      return insertAt
    }
    // No explicit position — append after the last node of the same
    // kind (or at the front of the next kind's block if none exist yet),
    // so a source added via "Find"'s own (+) lands at the end of Find,
    // not at the very front of the whole canvas.
    let lastOfKind = -1
    for (let i = 0; i < _nodes.value.length; i++) {
      if (_nodes.value[i].kind === def.kind) lastOfKind = i
    }
    if (lastOfKind !== -1) {
      _nodes.value.splice(lastOfKind + 1, 0, node)
      return lastOfKind + 1
    }
    if (def.kind === 'source') {
      _nodes.value.unshift(node)
      return 0
    }
    _nodes.value.push(node)
    return _nodes.value.length - 1
  }

  /** Remove a node by index. */
  function removeNode(index: number) {
    _nodes.value.splice(index, 1)
  }

  /** Swap a node with its same-kind neighbor in the given direction.
   *  Kept within the neighbor's kind so the sources-before-filters-
   *  before-prospect-before-actions ordering the rest of the draft
   *  (and the backend's positional filter order) relies on is never
   *  broken by a cross-group move. */
  function moveNode(index: number, direction: 'up' | 'down') {
    const nodes = _nodes.value
    const node = nodes[index]
    if (!node) return
    const neighborIndex = direction === 'up' ? index - 1 : index + 1
    const neighbor = nodes[neighborIndex]
    if (!neighbor || neighbor.kind !== node.kind) return
    nodes[index] = neighbor
    nodes[neighborIndex] = node
    _nodes.value = [...nodes]
  }

  /** Update a node's config (called by drawer cards on Apply). */
  function updateNodeConfig(nodeIndex: number, newConfig: Record<string, any>) {
    const node = _nodes.value[nodeIndex]
    if (!node) return
    node.config = { ...node.config, ...newConfig }
  }

  /** Revert the draft to the last-saved state. */
  function cancel() {
    _nodes.value = JSON.parse(_savedSnapshot.value || '[]')
    _error.value = ''
  }

  /** Call BEFORE save() whenever the sequence node changed. Returns null if
   *  there's nothing for the sequence to commit (no live action, or only
   *  the agent/sample-emails changed — those have no enrollment impact).
   *  Returns the affected/unaffected counts otherwise, so the caller can
   *  decide whether to prompt for a disposition before calling save(). */
  async function previewSequenceImpact(): Promise<{ affectedCount: number; unaffectedCount: number } | null> {
    const intent = computeSequenceCommitIntent(_nodes.value, _savedSnapshot.value, _actionIds.value)
    if (!intent.needsCommit || !intent.stepsChanged || !intent.actionId) return null
    const result: any = await apiFetch(
      `/sales-plays/${enc(_playId.value)}/actions/${enc(intent.actionId)}/sequence-preview`,
      { method: 'POST', body: { steps: intent.stepsBody } },
    )
    return {
      affectedCount: result?.affected_enrollment_count || 0,
      unaffectedCount: result?.unaffected_enrollment_count || 0,
    }
  }

  /** Persist the entire draft as ONE PATCH (additions/updates plus
   *  `body.delete` for removals), so the whole save is a single
   *  SalesPlayVersion. `disposition` only matters if the sequence's steps
   *  changed for an already-live sequence — the caller must already have
   *  confirmed it with the user (via previewSequenceImpact) when enrollments
   *  are affected; save() itself never prompts. */
  async function save(disposition: 'exit' | 'exit_and_reenroll' = 'exit'): Promise<boolean> {
    _saving.value = true
    _error.value = ''
    try {
      const playPath = `/sales-plays/${enc(_playId.value)}`
      const savedNodes: CanvasNode[] = JSON.parse(_savedSnapshot.value || '[]')
      // Keyed by backend id when the node has one (sources/filters can
      // repeat the same type, so `kind:type` alone would collide) —
      // falls back to `kind:type` only for nodes with no id yet, which by
      // definition have no saved counterpart to find anyway.
      const savedByKey = new Map<string, CanvasNode>()
      for (const n of savedNodes) {
        const key = n.config.id ? `${n.kind}:id:${n.config.id}` : `${n.kind}:${n.actionType || n.type}`
        savedByKey.set(key, n)
      }
      const savedNode = (n: CanvasNode) => savedByKey.get(
        n.config.id ? `${n.kind}:id:${n.config.id}` : `${n.kind}:${n.actionType || n.type}`,
      )
      const configChanged = (n: CanvasNode) => JSON.stringify(n.config) !== JSON.stringify(savedNode(n)?.config)

      const body: Record<string, any> = {}
      // Matches SalesPlayDeleteRequest; attached to body.delete before the PATCH fires.
      const del: Record<string, any> = {}

      // ── Sources (source nodes) ───────────────────────────────────
      // Any number of source nodes, each running independently — same
      // upsert-changed-only / create-if-new / delete-if-removed shape as
      // filters below, keyed by `config.id` rather than a single ref.
      const sourceNodes = _nodes.value.filter((n) => n.kind === 'source')
      const sourceBody = (n: CanvasNode) => {
        const sourceType = n.config.type || n.type
        const innerDef = { ...n.config }
        delete innerDef.id
        delete innerDef.type  // type lives in the envelope, not the inner definition
        return {
          source_id: n.config.id || undefined,
          source_type: sourceType,
          definition: {
            type: sourceType,
            display_name: n.config.name || '',
            definition: innerDef,
          },
        }
      }
      const changedSources = sourceNodes.filter((n) => !n.config.id || configChanged(n))
      if (changedSources.length > 0) {
        body.sources = changedSources.map(sourceBody)
      }

      // Sources removed from the canvas.
      const draftSourceIds = new Set(sourceNodes.filter((n) => n.config.id).map((n) => n.config.id))
      const removedSourceIds = [..._savedSourceIds.value].filter((id) => !draftSourceIds.has(id))
      if (removedSourceIds.length > 0) {
        del.sources = removedSourceIds
      }

      // ── Filters (filter nodes) ────────────────────────────────
      // Filter order lives on the play document, not on any individual
      // filter, so reordering is a separate call that never bumps a
      // filter's own version — see
      // docs/design/sales-play-canvas-granular-editing.md.
      const filterNodes = _nodes.value.filter((n) => n.kind === 'filter')
      const filterBody = (n: CanvasNode) => {
        const name = n.config.name || ''
        const inner: Record<string, any> = n.type === 'icp'
          ? {}
          : { ...(n.config.definition || n.config) }
        delete inner.id
        if (n.config.fail_on_missing_data === false) inner.fail_on_missing_data = false
        if (n.config.mode) inner.mode = n.config.mode
        return {
          filter_id: n.config.id || undefined,
          filter_type: n.type,
          definition: { type: n.type, display_name: name, definition: inner },
        }
      }
      const changedFilters = filterNodes.filter((n) => !n.config.id || configChanged(n))
      if (changedFilters.length > 0) {
        body.filters = changedFilters.map(filterBody)
      }

      // Filters removed from the canvas.
      const draftFilterIds = new Set(filterNodes.filter((n) => n.config.id).map((n) => n.config.id))
      const removedFilterIds = _savedFilterOrder.value.filter((id) => !draftFilterIds.has(id))
      if (removedFilterIds.length > 0) {
        del.filters = removedFilterIds
      }

      // ── Prospecting agent (prospect node) ──────────────────────
      // The prospect node's identity IS the play; there's no separate agent name to save.
      const prospectNode = _nodes.value.find((n) => n.kind === 'prospect')
      if (prospectNode) {
        if (!savedNode(prospectNode) || configChanged(prospectNode)) {
          body.prospecting_agent = {
            approach: prospectNode.config.approach || undefined,
            max_contacts_per_account: prospectNode.config.max_contacts_per_account ?? null,
            cadence_config: {
              active_days: prospectNode.config.active_days,
              opportunistic_days: prospectNode.config.opportunistic_days,
            },
          }
        }
        _hadProspectingAgent.value = true
      } else if (_hadProspectingAgent.value) {
        // Prospect node was removed from canvas — remove the prospecting agent.
        del.prospecting_agent = true
        _hadProspectingAgent.value = false
      }

      // ── ICP (filter node) ────────────────────────────────────
      const icpNode = _nodes.value.find((n) => n.type === 'icp')
      if (icpNode) {
        const savedIcp = savedNode(icpNode)
        const icpContentChanged = !savedIcp
          || JSON.stringify(icpNode.config.icp || {}) !== JSON.stringify(savedIcp.config.icp || {})
        if (icpContentChanged) {
          body.icp = { config: icpNode.config.icp || {} }
        }
      }

      // ── Personas (prospect node) ──────────────────────────────
      if (prospectNode) {
        const draftPersonas: any[] = prospectNode.config.personas || []
        const draftIds = new Set(draftPersonas.filter((p) => p.id).map((p) => p.id))

        // Removed personas.
        const removedPersonaIds = [..._savedPersonaIds.value].filter((id) => !draftIds.has(id))
        if (removedPersonaIds.length > 0) {
          del.personas = { personas: removedPersonaIds }
        }

        // New personas only — existing ones have no PATCH endpoint yet, so
        // they're left untouched (PersonasConfig.personas is upsert-only).
        const newPersonas = draftPersonas.filter((p) => !p.id)
        if (newPersonas.length > 0) {
          body.personas = {
            personas: newPersonas.map((p) => ({
              name: p.name,
              description: p.description || '',
              explicit_titles: p.explicit_titles || [],
              titles_to_exclude: p.titles_to_exclude || [],
            })),
          }
        }
      }

      // ── Sequence action (sequence node) ───────────────────────
      // Agent (sender identity) + sample emails fold into the unified
      // PATCH — no enrollment impact. Steps/name/description are
      // different: once the action already exists, they go through
      // sequence-commit instead (see computeSequenceCommitIntent), so live
      // enrollments are exited/handled rather than silently broken by a
      // plain write (see docs/design/sales-play-canvas-granular-editing.md).
      // The caller (previewSequenceImpact) is responsible for confirming
      // disposition with the user BEFORE calling save() when this would
      // affect live enrollments — by the time we get here it's decided.
      const sequenceNode = _nodes.value.find((n) => n.actionType === 'sequence')
      const sequenceSaved = sequenceNode ? savedNode(sequenceNode) : undefined
      const sequenceChanged = !!sequenceNode && (!sequenceSaved || configChanged(sequenceNode))
      const sequenceIntent = computeSequenceCommitIntent(_nodes.value, _savedSnapshot.value, _actionIds.value)
      const sequenceCommitPending = sequenceIntent.needsCommit
      if (sequenceNode && sequenceChanged) {
        const agentChanged = !sequenceSaved
          || sequenceNode.config.sender_approach !== sequenceSaved.config.sender_approach

        if (_actionIds.value.sequence) {
          if (agentChanged) {
            body.actions = [
              ...(body.actions || []),
              {
                action_type: 'sequence',
                action_id: _actionIds.value.sequence,
                agent: {
                  approach: sequenceNode.config.sender_approach || undefined,
                },
              },
            ]
          }
        } else if ((sequenceNode.config.steps?.length || 0) > 0) {
          // No action yet — create it via the unified PATCH (agent +
          // sequence together). No enrollments can exist yet, so this
          // path never hits the enrollment-impact guard. `agent.name` is
          // required server-side to create the row, but it's never
          // read back as the display value — the API always substitutes
          // the play's own name (see _resolve_sequence_action_definition)
          // — so any placeholder is fine here.
          const stepsBody = sequenceNode.config.steps.map((s: any) => ({
            step_index: s.step_index,
            step_type: s.step_type,
            brief: s.step_type !== 'wait' ? (s.brief?.trim() || null) : null,
            wait_days: s.step_type === 'wait' ? (s.wait_days || 3) : null,
            wait_unit: s.step_type === 'wait' ? (s.wait_unit || 'days') : null,
          }))
          body.actions = [
            ...(body.actions || []),
            {
              action_type: 'sequence',
              agent: {
                name: 'Sequence Agent',
                approach: sequenceNode.config.sender_approach || undefined,
              },
              sequence: {
                name: sequenceNode.config.name || 'Sequence',
                description: sequenceNode.config.sequence_description || null,
                steps: stepsBody,
              },
            },
          ]
        }
      } else if (!sequenceNode && _actionIds.value.sequence) {
        // Sequence node was removed from canvas — remove the action.
        // Deleting a sequence with live enrollments fails server-side; the
        // caller has to clone/exit those first (same rule as before).
        await apiFetch(
          `${playPath}/actions/${enc(_actionIds.value.sequence)}`,
          { method: 'DELETE' },
        ).catch(() => {})
        const { sequence: _dropped, ...rest } = _actionIds.value
        _actionIds.value = rest
        _sequenceId.value = null
        _sequenceAgentId.value = null
      }

      // ── Scheduling override (sequence node's drawer, lives on the ContactSequence) ──
      const schedulingChanged = !sequenceSaved
        || sequenceNode?.config.scheduling_customized !== sequenceSaved.config.scheduling_customized
        || sequenceNode?.config.scheduling_timezone_aware_sending !== sequenceSaved.config.scheduling_timezone_aware_sending
        || sequenceNode?.config.scheduling_default_us_timezone !== sequenceSaved.config.scheduling_default_us_timezone
        || sequenceNode?.config.scheduling_default_send_timezone !== sequenceSaved.config.scheduling_default_send_timezone
      if (sequenceNode && _actionIds.value.sequence && schedulingChanged) {
        const schedulingBody = sequenceNode.config.scheduling_customized
          ? {
              scheduling_override: {
                timezone_aware_sending: !!sequenceNode.config.scheduling_timezone_aware_sending,
                default_us_timezone: sequenceNode.config.scheduling_default_us_timezone || null,
                default_send_timezone: sequenceNode.config.scheduling_default_send_timezone || null,
              },
            }
          : { clear_scheduling_override: true }
        const existingSequenceAction = (body.actions || []).find(
          (a: any) => a.action_type === 'sequence' && a.action_id === _actionIds.value.sequence,
        )
        if (existingSequenceAction) {
          existingSequenceAction.sequence = { ...(existingSequenceAction.sequence || {}), ...schedulingBody }
        } else {
          body.actions = [
            ...(body.actions || []),
            { action_type: 'sequence', action_id: _actionIds.value.sequence, sequence: schedulingBody },
          ]
        }
      }

      // ── Sample emails (sequence node) — fold into the sequence action ──
      // Upsert-only, same contract as filters/personas: omission never
      // means delete. Sent as {id?, body} pairs (not bare strings) and
      // limited to new/changed emails, so an existing id updates that
      // row's body in place (one new version for the edited email only)
      // and the diff shows just what actually changed.
      let removedSampleEmailIds: string[] = []
      if (sequenceNode) {
        const draftEmails: any[] = sequenceNode.config.sample_emails || []
        const savedEmails: any[] = sequenceSaved?.config.sample_emails || []
        const savedEmailById = new Map(savedEmails.filter((e) => e.id).map((e) => [e.id, e]))
        const draftEmailIds = new Set(draftEmails.filter((e) => e.id).map((e) => e.id))

        const changedEmails = draftEmails.filter((e) => !e.id || savedEmailById.get(e.id)?.body !== e.body)
        if (changedEmails.length > 0) {
          const sampleEmailsBody = changedEmails.map((e) => (e.id ? { id: e.id, body: e.body } : { body: e.body }))
          const existingSequenceAction = (body.actions || []).find((a: any) => a.action_type === 'sequence')
          if (existingSequenceAction) {
            existingSequenceAction.sample_emails = sampleEmailsBody
          } else if (_actionIds.value.sequence) {
            body.actions = [
              ...(body.actions || []),
              { action_type: 'sequence', action_id: _actionIds.value.sequence, sample_emails: sampleEmailsBody },
            ]
          }
        }

        removedSampleEmailIds = savedEmails
          .filter((e) => e.id && !draftEmailIds.has(e.id))
          .map((e) => e.id)
        if (removedSampleEmailIds.length > 0 && _actionIds.value.sequence) {
          del.actions = [
            ...(del.actions || []),
            { action_id: _actionIds.value.sequence, sample_emails: removedSampleEmailIds },
          ]
        }
      }

      // ── Dialer action (dialer node) ───────────────────────────
      // No config of its own — presence on the play is the whole point.
      const dialerNode = _nodes.value.find((n) => n.actionType === 'dialer')
      if (dialerNode && !_actionIds.value.dialer) {
        body.actions = [...(body.actions || []), { action_type: 'dialer', definition: {} }]
      } else if (!dialerNode && _actionIds.value.dialer) {
        del.actions = [
          ...(del.actions || []),
          { action_id: _actionIds.value.dialer, delete_action: true },
        ]
      }

      // ── Reorder (filters/sources) — folded into the same PATCH ──────
      // A node not yet saved (no config.id) has no real id to put in the
      // order list — it references its own position in body.filters /
      // body.sources instead, via a "new:<i>" placeholder the backend
      // resolves against what body.update just created. This is what lets
      // e.g. "insert a new filter at the top" land as ONE version instead
      // of a create-version followed by a reorder-version.
      function newItemPlaceholders(changedNodes: CanvasNode[]): Map<CanvasNode, string> {
        const placeholders = new Map<CanvasNode, string>()
        let newIndex = 0
        for (const n of changedNodes) {
          if (!n.config.id) placeholders.set(n, `new:${newIndex++}`)
        }
        return placeholders
      }
      const newFilterPlaceholders = newItemPlaceholders(changedFilters)
      const newSourcePlaceholders = newItemPlaceholders(changedSources)

      const fullFilterOrder = filterNodes.map((n) => n.config.id || newFilterPlaceholders.get(n)!)
      const survivingSavedOrder = _savedFilterOrder.value.filter((id) => draftFilterIds.has(id))
      const filterOrderChanged = filterNodes.some((n) => !n.config.id)
        || filterNodes.filter((n) => n.config.id).map((n) => n.config.id).some((id, i) => id !== survivingSavedOrder[i])

      const fullSourceOrder = sourceNodes.map((n) => n.config.id || newSourcePlaceholders.get(n)!)
      const survivingSavedSourceOrder = _savedSourceOrder.value.filter((id) => draftSourceIds.has(id))
      const sourceOrderChanged = sourceNodes.some((n) => !n.config.id)
        || sourceNodes.filter((n) => n.config.id).map((n) => n.config.id).some((id, i) => id !== survivingSavedSourceOrder[i])

      const reorder: Record<string, any> = {}
      if (filterOrderChanged && fullFilterOrder.length > 0) reorder.filter_ids = fullFilterOrder
      if (sourceOrderChanged && fullSourceOrder.length > 0) reorder.source_ids = fullSourceOrder

      // ── One sparse PATCH for everything gathered above ──────────
      // update/delete/reorder are peers on the wire.
      const patchBody: Record<string, any> = {}
      if (Object.keys(body).length > 0) patchBody.update = body
      if (Object.keys(del).length > 0) patchBody.delete = del
      if (Object.keys(reorder).length > 0) patchBody.reorder = reorder
      if (Object.keys(patchBody).length > 0) {
        const resp: any = await apiFetch(playPath, { method: 'PATCH', body: patchBody })
        const savedActions = resp?.play?.actions || resp?.actions || []
        const savedSequence = savedActions.find((a: any) => a.action_type === 'sequence')
        if (savedSequence) {
          _actionIds.value = { ..._actionIds.value, sequence: savedSequence.id }
          _sequenceAgentId.value = savedSequence.definition?.agent?.id || _sequenceAgentId.value
          _sequenceId.value = savedSequence.definition?.contact_sequence_id || _sequenceId.value
          if (sequenceNode) sequenceNode.config.sequence_id = _sequenceId.value
        }
        const savedDialer = savedActions.find((a: any) => a.action_type === 'dialer')
        if (savedDialer) {
          _actionIds.value = { ..._actionIds.value, dialer: savedDialer.id }
        }
        if (!dialerNode && _actionIds.value.dialer) {
          const { dialer: _droppedDialer, ...restAfterDialerDelete } = _actionIds.value
          _actionIds.value = restAfterDialerDelete
        }
        const savedPersonas = resp?.play?.personas?.local || resp?.personas?.local || []
        if (prospectNode) {
          for (const p of prospectNode.config.personas || []) {
            if (!p.id) {
              const match = savedPersonas.find((sp: any) => sp.name === p.name && !_savedPersonaIds.value.has(sp.id))
              if (match) p.id = match.id
            }
          }
        }
        // New sources/filters have no id to match by content (two of the
        // same type can be identical), so match by exclusion instead:
        // whichever saved rows aren't in the prior known-id set must be
        // the ones just created, in the same relative order they were
        // sent — reordering can interleave them with existing rows, but
        // doesn't change a new item's order relative to other new items.
        const savedSources = resp?.play?.sources || resp?.sources || []
        const newSourceNodes = changedSources.filter((n) => !n.config.id)
        if (newSourceNodes.length > 0) {
          const unmatchedSaved = savedSources.filter((s: any) => !_savedSourceIds.value.has(s.id))
          newSourceNodes.forEach((n, i) => {
            const match = unmatchedSaved[i]
            if (match) n.config.id = match.id
          })
        }
        const savedFilters = resp?.play?.filters || resp?.filters || []
        const newFilterNodes = changedFilters.filter((n) => !n.config.id)
        if (newFilterNodes.length > 0) {
          const knownFilterIds = new Set(_savedFilterOrder.value)
          const unmatchedSavedFilters = savedFilters.filter((f: any) => !knownFilterIds.has(f.id))
          newFilterNodes.forEach((n, i) => {
            const match = unmatchedSavedFilters[i]
            if (match) n.config.id = match.id
          })
        }
      }

      // ── Sequence step commit (enrollment-aware, separate write) ────
      // disposition was already confirmed with the user (if needed) by
      // previewSequenceImpact before save() was ever called.
      if (sequenceCommitPending && sequenceNode && sequenceIntent.actionId) {
        const commitResp: any = await apiFetch(
          `${playPath}/actions/${enc(sequenceIntent.actionId)}/sequence-commit`,
          {
            method: 'POST',
            body: {
              name: sequenceIntent.name,
              description: sequenceIntent.description,
              steps: sequenceIntent.stepsBody,
              disposition,
            },
          },
        )
        _sequenceId.value = commitResp?.sequence_id || _sequenceId.value
        sequenceNode.config.sequence_id = _sequenceId.value
      }


      // ── Update saved snapshot ─────────────────────────────────
      _savedSnapshot.value = snapshot()

      // Update saved ID sets for next diff.
      _savedPersonaIds.value = new Set<string>()
      _savedFilterOrder.value = _nodes.value
        .filter((n) => n.kind === 'filter' && n.config.id)
        .map((n) => n.config.id)
      _savedSourceIds.value = new Set<string>(
        _nodes.value.filter((n) => n.kind === 'source' && n.config.id).map((n) => n.config.id),
      )
      _savedSourceOrder.value = _nodes.value
        .filter((n) => n.kind === 'source' && n.config.id)
        .map((n) => n.config.id)
      for (const node of _nodes.value) {
        if (node.kind === 'prospect') {
          for (const p of node.config.personas || []) {
            if (p.id) _savedPersonaIds.value.add(p.id)
          }
        }
      }

      return true
    } catch (e: any) {
      _error.value = e?.message || 'Save failed'
      return false
    } finally {
      _saving.value = false
    }
  }

  return {
    nodes,
    dirty,
    saving,
    error,
    pipelineId: computed(() => _pipelineId.value),
    actionIds: computed(() => _actionIds.value),
    stepTypes: computed(() => _stepTypes.value),
    initialize,
    refreshFromBackend,
    addNode,
    removeNode,
    moveNode,
    updateNodeConfig,
    cancel,
    save,
    previewSequenceImpact,
  }
}

// ── Helpers ─────────────────────────────────────────────────────

function enc(s: string) {
  return encodeURIComponent(s)
}

interface SequenceCommitIntent {
  needsCommit: boolean
  stepsChanged: boolean
  actionId: string | null
  stepsBody: Record<string, any>[]
  name: string
  description: string | null
}

/** Pure: does the current sequence node need a sequence-commit call (steps,
 *  name, or description changed on an already-existing sequence action),
 *  and if so what would it send? Used both to gate a pre-save impact
 *  preview and, once confirmed, by save() itself — one source of truth so
 *  the preview and the real commit can never see a different question. */
function computeSequenceCommitIntent(
  nodes: CanvasNode[], savedSnapshotJson: string, actionIds: Record<string, string>,
): SequenceCommitIntent {
  const none: SequenceCommitIntent = {
    needsCommit: false, stepsChanged: false, actionId: null, stepsBody: [], name: '', description: null,
  }
  const sequenceNode = nodes.find((n) => n.actionType === 'sequence')
  if (!sequenceNode || !actionIds.sequence) return none

  const savedNodes: CanvasNode[] = JSON.parse(savedSnapshotJson || '[]')
  const sequenceSaved = savedNodes.find((n) => n.actionType === 'sequence')

  const stepsChanged = !sequenceSaved
    || JSON.stringify(sequenceNode.config.steps || []) !== JSON.stringify(sequenceSaved.config.steps || [])
  const nameOrDescriptionChanged = !sequenceSaved
    || sequenceNode.config.name !== sequenceSaved.config.name
    || sequenceNode.config.sequence_description !== sequenceSaved.config.sequence_description

  const needsCommit = (stepsChanged || nameOrDescriptionChanged)
    && (sequenceNode.config.steps?.length || 0) > 0
  if (!needsCommit) return none

  const stepsBody = sequenceNode.config.steps.map((s: any) => ({
    step_index: s.step_index,
    step_type: s.step_type,
    brief: s.step_type !== 'wait' ? (s.brief?.trim() || null) : null,
    wait_days: s.step_type === 'wait' ? (s.wait_days || 3) : null,
    wait_unit: s.step_type === 'wait' ? (s.wait_unit || 'days') : null,
  }))
  return {
    needsCommit: true,
    stepsChanged,
    actionId: actionIds.sequence,
    stepsBody,
    name: sequenceNode.config.name || 'Sequence',
    description: sequenceNode.config.sequence_description || null,
  }
}

/** Assemble canvas nodes back into the audience_definition shape
 *  expected by the pipeline API. */
function assembleAudienceDefinition(nodes: CanvasNode[]): Record<string, any> {
  let produce: Record<string, any> | null = null
  const filters: Record<string, any>[] = []

  for (const node of nodes) {
    if (node.kind === 'source') {
      produce = { ...node.config }
    } else if (node.kind === 'filter') {
      filters.push({ ...node.config })
    }
  }

  return {
    produce,
    filters,
  }
}

function hasNodesOfKind(nodes: CanvasNode[], kind: CanvasNodeKind): boolean {
  return nodes.some((n) => n.kind === kind)
}
